import { Mail, Unlink } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import type { FormField } from '@/types/api/form'
import { formatDateTime } from '@/utils/date'

import { fillSearchTemplate, formatEmailMessage, parseEmailMessage } from '../utils/emailMessage'

import { EmailPickerDialog } from './EmailPickerDialog'

/** The email a record is linked to, with buttons to pick another or unlink it. */
export function EmailMessageFieldInput({ id, field, value, onChange, disabled, valuesByKey }: {
  id: string
  field: Pick<FormField, 'options'>
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  /** The record's other values by field key, to fill the field's default search. */
  valuesByKey?: Record<string, string>
}) {
  const [picking, setPicking] = useState(false)
  const email = parseEmailMessage(value)
  const defaultSearch = typeof field.options?.defaultSearch === 'string' ? field.options.defaultSearch : ''

  return (
    <div id={id} className="space-y-2">
      {email ? (
        <div className="flex items-start gap-3 rounded-lg border border-neutral-200 p-3">
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{email.subject || '(no subject)'}</p>
            <p className="truncate text-xs text-neutral-600">{email.from}</p>
            {email.date && <p className="text-xs text-neutral-400">{formatDateTime(email.date)}</p>}
          </div>
        </div>
      ) : value ? (
        <p className="text-sm text-neutral-500">This value is not a linked email.</p>
      ) : (
        <p className="text-sm text-neutral-500">No email linked.</p>
      )}
      {!disabled && (
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPicking(true)}>
            {email ? 'Change email' : 'Pick email'}
          </Button>
          {value && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')}>
              <Unlink className="mr-1.5 h-3.5 w-3.5" />Unlink
            </Button>
          )}
        </div>
      )}
      {picking && (
        <EmailPickerDialog
          initialQuery={fillSearchTemplate(defaultSearch, valuesByKey ?? {})}
          initialConnectionId={email?.connectionId}
          onClose={() => setPicking(false)}
          onPick={(picked) => {
            onChange(formatEmailMessage(picked))
            setPicking(false)
          }}
        />
      )}
    </div>
  )
}
